import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hotel } from 'lucide-react';
import { getAllowanceRecent } from '../lib/api';

/**
 * AllowanceRecentEntries
 *
 * Displays up to `limit` recent allowance entries fetched from
 * GET /api/expenses/allowance/recent. Clicking a card fires onSelect(entry)
 * so the parent form can auto-fill its fields (type/destination/country/
 * nights/meals/international rates — NOT start/end datetime, which stay
 * derived from the day being added to).
 *
 * Mirrors MileageRecentEntries's self-contained fetch/error/empty handling,
 * but defaults to showing fewer entries (2 vs mileage's 3) per product
 * direction — allowance trips repeat less often than routine mileage routes.
 *
 * Renders nothing if: still loading, fetch failed (e.g. the backend endpoint
 * isn't live yet), or the user has no history.
 *
 * Props:
 *   onSelect  {function(entry)}  — called with the full API entry object
 *   limit     {number}           — max entries to fetch (default 2)
 */
export default function AllowanceRecentEntries({ onSelect, limit = 2 }) {
    const { t } = useTranslation();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        getAllowanceRecent(limit)
            .then((res) => {
                if (!cancelled) {
                    setEntries(res.data || []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [limit]);

    // Render nothing while loading or on error or when there is no history
    if (loading || error || entries.length === 0) return null;

    return (
        <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {t('expenses.allowance.recent_entries')}
            </p>
            <div className="flex flex-wrap gap-2">
                {entries.map((entry, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onSelect(entry)}
                        aria-label={`Use allowance ${entry.destination || entry.allowance_type}, ${entry.country || ''}, last used ${entry.last_used}`}
                        className="w-full sm:w-auto flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors min-h-[44px]"
                    >
                        <Hotel className="w-4 h-4 shrink-0 text-muted-foreground" />
                        <span className="flex flex-col min-w-0">
                            <span className="font-medium truncate">
                                {t(`expenses.allowance_type.${entry.allowance_type}`, entry.allowance_type)}
                                {entry.destination ? ` — ${entry.destination}` : ''}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {entry.country ? `${entry.country} · ` : ''}{entry.last_used}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
